import React from "react";
import { VB_TOKENS, VB_FONTS } from "./tokens";
import { clamp, smooth } from "./mathUtils";
import { useReducedMotion } from "./clock";

// =============================================================================
// VaultDoor — production port of VaultDoor.jsx
//
// Circular bank-vault door with clock-face proportions:
//   - Brushed steel body + bezel ring + decorative gold filigree band
//   - 20 hex-slot bolts arranged in a ring around the rim
//   - 5 keyholes arranged in a pentagon, each with state-driven glow + smoke
//   - Central rotating combination dial with 60 notches and labels
//   - Twin barrel-style hinges on the right edge (door swings around them)
//   - When `openMode` is set, a tunnel is rendered behind the door so the
//     swinging door reveals warm gold (win) or cold cyan (bust) inside.
// =============================================================================

const VB_TAU = Math.PI * 2;

// ----- Public types ---------------------------------------------------------

export type KeyState = "idle" | "arming" | "flared" | "locked";

export interface VaultDoorState {
  /** Wall-clock time in seconds — used to drive sin/cos pulses. */
  t: number;
  /** Combination dial rotation, in degrees. */
  dialAngle: number;
  /** 0..1 ambient cyan glow intensity. */
  tension: number;
  /** 0..1 red danger pulse intensity (drives center-boss flash). */
  danger: number;
  /** 5-element list of per-keyhole socket states. */
  keyStates: KeyState[];
  /** 5-element list — true if a key is sitting in the corresponding hole. */
  hasKeys: boolean[];
  /** 5-element list of 0..1 turn progress values (0 = horizontal, 1 = 90°). */
  keyAngles: number[];
  /** 5-element list of 0..1 smoke-wisp opacity values. */
  keySmokes: number[];
  /** When defined the door is opening; selects tunnel + swing direction. */
  openMode?: "win" | "bust";
  /** 0..1 door opening progress; only meaningful when `openMode` is set. */
  openProgress: number;
}

export interface VaultDoorProps {
  state: VaultDoorState;
  /** Square pixel size of the door. Defaults to 560. */
  size?: number;
}

// ----- FiligreeRing ---------------------------------------------------------
// Decorative scalloped + dotted ring rendered as a single quadratic-Bézier
// path with a small `<circle>` at each scallop dip.
// Source: VaultDoor.jsx:87-115.

interface FiligreeRingProps {
  r: number;
  count?: number;
  amplitude?: number;
}

export function FiligreeRing({
  r,
  count = 24,
  amplitude = 8,
}: FiligreeRingProps): JSX.Element {
  const segments: string[] = [];
  const dots: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i++) {
    const a0 = (i / count) * VB_TAU;
    const a1 = ((i + 1) / count) * VB_TAU;
    const aMid = (a0 + a1) / 2;
    const x0 = Math.cos(a0) * r;
    const y0 = Math.sin(a0) * r;
    const xMid = Math.cos(aMid) * (r - amplitude);
    const yMid = Math.sin(aMid) * (r - amplitude);
    const x1 = Math.cos(a1) * r;
    const y1 = Math.sin(a1) * r;
    if (i === 0) segments.push(`M ${x0} ${y0}`);
    segments.push(`Q ${xMid} ${yMid} ${x1} ${y1}`);
    dots.push({ x: xMid, y: yMid });
  }
  segments.push("Z");
  return (
    <g>
      <path
        d={segments.join(" ")}
        fill="none"
        stroke="url(#vd-gold)"
        strokeWidth={1.6}
        opacity={0.9}
      />
      {dots.map((d, i) => (
        <circle
          key={i}
          cx={d.x}
          cy={d.y}
          r={1.4}
          fill="url(#vd-goldShine)"
        />
      ))}
    </g>
  );
}

// ----- BoltRing -------------------------------------------------------------
// Ring of hex-slot bolts. Each bolt = (outer disk, inner disk, slot line,
// inner ring) = 4 primitives. Source: VaultDoor.jsx:118-140.

interface BoltRingProps {
  r: number;
  count?: number;
  size?: number;
}

export function BoltRing({
  r,
  count = 16,
  size = 5,
}: BoltRingProps): JSX.Element {
  const T = VB_TOKENS;
  const bolts: { x: number; y: number; key: number }[] = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * VB_TAU - Math.PI / 2;
    bolts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r, key: i });
  }
  return (
    <g>
      {bolts.map((b) => (
        <g key={b.key} transform={`translate(${b.x},${b.y})`}>
          <circle
            r={size}
            fill={T.steel1}
            stroke={T.steel0}
            strokeWidth={0.8}
          />
          <circle r={size - 1.2} fill="url(#vd-rim)" opacity={0.85} />
          <line
            x1={-size * 0.55}
            y1={0}
            x2={size * 0.55}
            y2={0}
            stroke={T.steel0}
            strokeWidth={0.7}
          />
          <circle
            r={size * 0.35}
            fill="none"
            stroke={T.steel5}
            strokeWidth={0.4}
            opacity={0.6}
          />
        </g>
      ))}
    </g>
  );
}

// ----- CentralDial ----------------------------------------------------------
// Central rotating combination dial. 60 notches (every 5th major), 4 numerical
// labels at 0/15/30/45, fixed pointer, 5 nested center-boss circles, ambient
// cyan halo, optional red danger pulse. Source: VaultDoor.jsx:143-232.

interface CentralDialProps {
  r: number;
  dialAngle: number;
  glow: number;
  danger: number;
  /** Wall-clock time in seconds (for the danger pulse). */
  t: number;
  reduced: boolean;
}

export function CentralDial({
  r,
  dialAngle,
  glow,
  danger,
  t,
  reduced,
}: CentralDialProps): JSX.Element {
  const T = VB_TOKENS;

  interface Notch {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    major: boolean;
    key: number;
  }
  const notches: Notch[] = [];
  for (let i = 0; i < 60; i++) {
    const major = i % 5 === 0;
    const a = (i / 60) * VB_TAU - Math.PI / 2;
    const r1 = r - (major ? 14 : 8);
    const r2 = r - 4;
    notches.push({
      x1: Math.cos(a) * r1,
      y1: Math.sin(a) * r1,
      x2: Math.cos(a) * r2,
      y2: Math.sin(a) * r2,
      major,
      key: i,
    });
  }

  const cyanGlow = (1 - danger) * (0.4 + glow * 0.6);
  const redGlow = reduced
    ? danger > 0
      ? 1
      : 0
    : danger * (0.5 + 0.5 * Math.sin(t * 6));

  return (
    <g>
      {/* Recessed dial plate */}
      <circle r={r + 4} fill={T.steel0} />
      <circle
        r={r}
        fill="url(#vd-dialPlate)"
        filter="url(#vd-innerShadow)"
      />

      {/* Outer notch ring (rotates with the dial) */}
      <g transform={`rotate(${dialAngle})`}>
        {notches.map((n) => (
          <line
            key={n.key}
            x1={n.x1}
            y1={n.y1}
            x2={n.x2}
            y2={n.y2}
            stroke={n.major ? T.cyan3 : T.cyan1}
            strokeWidth={n.major ? 1.8 : 1}
            opacity={n.major ? 0.9 : 0.55}
            style={{
              filter: `drop-shadow(0 0 ${n.major ? 3 : 1.5}px ${T.cyan3})`,
            }}
          />
        ))}

        {/* Major numerical labels at 0 / 15 / 30 / 45 */}
        {[0, 15, 30, 45].map((deg) => {
          const a = (deg / 60) * VB_TAU - Math.PI / 2;
          const rr = r - 26;
          const x = Math.cos(a) * rr;
          const y = Math.sin(a) * rr;
          return (
            <text
              key={deg}
              x={x}
              y={y + 4}
              textAnchor="middle"
              fontFamily={VB_FONTS.mono}
              fontSize={11}
              fill={T.cyan4}
              opacity={0.9}
              style={{ filter: `drop-shadow(0 0 4px ${T.cyan3})` }}
            >
              {String(deg).padStart(2, "0")}
            </text>
          );
        })}
      </g>

      {/* Fixed pointer triangle at the top */}
      <path
        d={`M 0 ${-r + 2} L -5 ${-r + 14} L 5 ${-r + 14} Z`}
        fill={T.gold4}
        stroke={T.gold0}
        strokeWidth={0.5}
      />

      {/* Center boss — 5 nested circles */}
      <circle r={r * 0.3} fill={T.steel0} />
      <circle r={r * 0.27} fill="url(#vd-dialPlate)" />
      <circle r={r * 0.18} fill="url(#vd-rim)" />
      <circle r={r * 0.12} fill="url(#vd-gold)" />
      <circle r={r * 0.05} fill={T.gold0} />

      {/* Cyan ambient halo */}
      <circle
        r={r}
        fill="none"
        stroke={T.cyan3}
        strokeWidth={0.8}
        opacity={cyanGlow * 0.7}
        style={{ filter: `drop-shadow(0 0 12px ${T.cyan3})` }}
      />

      {/* Red danger pulse (only when danger > 0) */}
      {danger > 0 && (
        <circle
          r={r + 2}
          fill="none"
          stroke={T.red2}
          strokeWidth={1.5}
          opacity={redGlow}
          style={{ filter: `drop-shadow(0 0 14px ${T.red2})` }}
        />
      )}
    </g>
  );
}

// ----- InsertedKeyArt -------------------------------------------------------
// Stylized small key sticking out of a locked keyhole. Source: VaultDoor.jsx:325-343.

interface InsertedKeyArtProps {
  index: number;
}

function InsertedKeyArt({ index: _index }: InsertedKeyArtProps): JSX.Element {
  const T = VB_TOKENS;
  return (
    <g>
      {/* Shaft going INTO the hole (down) */}
      <rect x={-1.2} y={-4} width={2.4} height={14} fill="url(#vd-gold)" />
      {/* Bow above */}
      <circle
        cy={-9}
        r={6}
        fill="url(#vd-goldShine)"
        stroke={T.gold0}
        strokeWidth={0.6}
      />
      <circle cy={-9} r={2.5} fill={T.void0} />
      {/* Ornate bow flourishes */}
      <circle cx={-7.5} cy={-9} r={1.5} fill="url(#vd-gold)" />
      <circle cx={7.5} cy={-9} r={1.5} fill="url(#vd-gold)" />
      <circle cy={-15.5} r={1.8} fill="url(#vd-gold)" />
      {/* Teeth at the bottom */}
      <rect x={-1.5} y={8} width={3.5} height={2.5} fill="url(#vd-gold)" />
      <rect x={-1.5} y={11} width={2} height={1.5} fill="url(#vd-gold)" />
    </g>
  );
}

// ----- Keyhole --------------------------------------------------------------
// One brass-rimmed socket in the pentagon. Eight base primitives, plus an
// optional inserted key (5 prims) and up to 3 smoke wisps. Source:
// VaultDoor.jsx:238-322.

interface KeyholeProps {
  angle: number;
  dist: number;
  index: number;
  state: KeyState;
  keyAngle: number;
  hasKey: boolean;
  smoke: number;
  t: number;
  reduced: boolean;
}

export function Keyhole({
  angle,
  dist,
  index,
  state,
  keyAngle,
  hasKey,
  smoke,
  t,
  reduced,
}: KeyholeProps): JSX.Element {
  const T = VB_TOKENS;
  const x = Math.cos(angle) * dist;
  const y = Math.sin(angle) * dist;

  // Pulse signals — disabled under reduced-motion.
  const pulseHB = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(t * 1.6 + index * 0.7);
  const pulseFast = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(t * 4);
  const flicker = reduced ? 1 : 0.85 + 0.15 * Math.sin(t * 24);

  const color =
    state === "arming"
      ? T.red2
      : state === "locked" || state === "flared"
        ? T.gold4
        : T.cyan3;

  const intensity =
    state === "arming"
      ? (0.6 + pulseFast * 0.4) * flicker
      : state === "locked"
        ? 0.95
        : state === "flared"
          ? 1.0
          : 0.35 + pulseHB * 0.3;

  // Rotate the whole keyhole so the keyhole shape points OUTWARD from center.
  const rotateDeg = (angle * 180) / Math.PI + 90;

  return (
    <g transform={`translate(${x},${y}) rotate(${rotateDeg})`}>
      {/* Outer brass socket */}
      <circle
        r={22}
        fill="url(#vd-gold)"
        stroke={T.gold0}
        strokeWidth={1}
      />
      <circle
        r={22}
        fill="none"
        stroke="url(#vd-goldShine)"
        strokeWidth={0.8}
        opacity={0.7}
      />
      {/* Inner recessed ring */}
      <circle r={17} fill={T.steel0} />
      <circle r={16} fill={T.void0} />

      {/* Glow halo */}
      <circle
        r={14}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
        opacity={intensity}
        style={{
          filter: `drop-shadow(0 0 ${8 + intensity * 8}px ${color})`,
        }}
      />

      {/* Inner light pool */}
      <circle r={10} fill={color} opacity={intensity * 0.18} />

      {/* The keyhole shape — only visible when no key is inserted */}
      {!hasKey && (
        <g opacity={state === "locked" ? 0.3 : 0.85}>
          <circle
            cy={-4}
            r={4}
            fill="#000"
            stroke={color}
            strokeWidth={0.6}
          />
          <path
            d="M 0 -2 L -3 8 L 3 8 Z"
            fill="#000"
            stroke={color}
            strokeWidth={0.6}
          />
        </g>
      )}

      {/* Inserted / turning key */}
      {hasKey && (
        <g transform={`rotate(${keyAngle * 90})`}>
          <InsertedKeyArt index={index} />
        </g>
      )}

      {/* Faint index number above the socket */}
      <text
        x={0}
        y={-30}
        textAnchor="middle"
        fontFamily={VB_FONTS.mono}
        fontSize={8}
        fill={T.gold3}
        opacity={0.7}
      >
        {String(index + 1).padStart(2, "0")}
      </text>

      {/* Smoke wisps drifting upward (3 circles) */}
      {smoke > 0 && (
        <g opacity={smoke}>
          {[0, 1, 2].map((i) => {
            const off = reduced ? (i * 0.33) % 1 : (t * 0.6 + i * 0.33) % 1;
            const cx = reduced ? 0 : Math.sin(t * 2 + i) * 4;
            return (
              <circle
                key={i}
                cx={cx}
                cy={-20 - off * 30}
                r={3 + off * 6}
                fill={T.red3}
                opacity={(1 - off) * 0.4}
                style={{ filter: "blur(4px)" }}
              />
            );
          })}
        </g>
      )}
    </g>
  );
}

// ----- Hinges ---------------------------------------------------------------
// Twin right-side barrel hinges. Each hinge = plate + inner stroke + barrel +
// highlight line + 3 bolts = 7 primitives. Source: VaultDoor.jsx:346-371.

interface HingesProps {
  outerR: number;
}

export function Hinges({ outerR }: HingesProps): JSX.Element {
  const T = VB_TOKENS;
  const yOffsets = [-outerR * 0.55, outerR * 0.55];
  return (
    <g>
      {yOffsets.map((y, i) => (
        <g key={i} transform={`translate(${outerR + 14},${y})`}>
          {/* Hinge plate */}
          <rect
            x={-12}
            y={-22}
            width={40}
            height={44}
            rx={3}
            fill="url(#vd-rim)"
            stroke={T.steel0}
            strokeWidth={1}
          />
          <rect
            x={-10}
            y={-20}
            width={36}
            height={40}
            rx={2}
            fill="none"
            stroke={T.steel4}
            strokeWidth={0.6}
            opacity={0.6}
          />
          {/* Hinge barrel */}
          <rect
            x={-4}
            y={-26}
            width={14}
            height={52}
            rx={6}
            fill="url(#vd-rim)"
            stroke={T.steel0}
            strokeWidth={1}
          />
          <line
            x1={3}
            y1={-24}
            x2={3}
            y2={24}
            stroke={T.steel5}
            strokeWidth={0.5}
            opacity={0.7}
          />
          {/* Bolts */}
          {[-15, 0, 15].map((by, j) => (
            <circle
              key={j}
              cx={14}
              cy={by}
              r={2.4}
              fill={T.steel1}
              stroke={T.steel0}
              strokeWidth={0.5}
            />
          ))}
        </g>
      ))}
    </g>
  );
}

// ----- VaultDefs ------------------------------------------------------------
// SVG <defs> block. Shared gradients, patterns, and filters used across the
// door. Allocated once per render. Source: VaultDoor.jsx:10-84.

interface VaultDefsProps {
  openMode?: "win" | "bust";
}

function VaultDefs({ openMode }: VaultDefsProps): JSX.Element {
  const T = VB_TOKENS;
  return (
    <defs>
      {/* Brushed steel — concentric rings via radial gradient + noise overlay */}
      <radialGradient id="vd-steel" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor={T.steel4} />
        <stop offset="0.35" stopColor={T.steel3} />
        <stop offset="0.65" stopColor={T.steel2} />
        <stop offset="0.92" stopColor={T.steel1} />
        <stop offset="1" stopColor={T.steel0} />
      </radialGradient>

      {/* Steel rim (outer bezel) — directional from upper-left light */}
      <linearGradient id="vd-rim" x1="0.1" y1="0.0" x2="0.9" y2="1.0">
        <stop offset="0" stopColor={T.steel5} />
        <stop offset="0.18" stopColor={T.steel4} />
        <stop offset="0.5" stopColor={T.steel2} />
        <stop offset="1" stopColor={T.steel0} />
      </linearGradient>

      {/* Inner ring (where the dial sits) */}
      <radialGradient id="vd-dialPlate" cx="0.4" cy="0.35" r="0.7">
        <stop offset="0" stopColor={T.steel4} />
        <stop offset="0.4" stopColor={T.steel2} />
        <stop offset="1" stopColor={T.steel0} />
      </radialGradient>

      {/* Gold filigree gradient */}
      <linearGradient id="vd-gold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={T.gold5} />
        <stop offset="0.45" stopColor={T.gold3} />
        <stop offset="0.55" stopColor={T.gold2} />
        <stop offset="1" stopColor={T.gold0} />
      </linearGradient>

      {/* Bright gold shine */}
      <linearGradient id="vd-goldShine" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={T.goldHot} />
        <stop offset="0.5" stopColor={T.gold4} />
        <stop offset="1" stopColor={T.gold1} />
      </linearGradient>

      {/* Tunnel-inside gradient — programmatic stops based on openMode */}
      <radialGradient id="vd-tunnelInner" cx="0.5" cy="0.5" r="0.5">
        {openMode === "win" ? (
          <>
            <stop offset="0" stopColor={T.goldHot} />
            <stop offset="0.2" stopColor={T.gold5} />
            <stop offset="0.5" stopColor={T.gold3} />
            <stop offset="0.85" stopColor={T.gold0} />
            <stop offset="1" stopColor="#000" />
          </>
        ) : openMode === "bust" ? (
          <>
            <stop offset="0" stopColor={T.cyan5} />
            <stop offset="0.25" stopColor={T.cyan3} />
            <stop offset="0.6" stopColor={T.cyan0} />
            <stop offset="1" stopColor="#000" />
          </>
        ) : (
          <>
            <stop offset="0" stopColor="#000" />
            <stop offset="1" stopColor="#000" />
          </>
        )}
      </radialGradient>

      {/* Brushed metal pattern — faint horizontal scanlines */}
      <pattern
        id="vd-brushed"
        patternUnits="userSpaceOnUse"
        width="2"
        height="2"
      >
        <rect width="2" height="2" fill="rgba(0,0,0,0)" />
        <line
          x1="0"
          y1="0"
          x2="2"
          y2="0"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="0.5"
        />
      </pattern>

      {/* Soft inner shadow filter */}
      <filter
        id="vd-innerShadow"
        x="-20%"
        y="-20%"
        width="140%"
        height="140%"
      >
        <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
        <feOffset dx="0" dy="4" />
        <feComposite
          in2="SourceAlpha"
          operator="arithmetic"
          k2="-1"
          k3="1"
        />
        <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.7 0" />
        <feComposite in2="SourceGraphic" operator="atop" />
      </filter>

      {/* Soft drop shadow */}
      <filter id="vd-drop" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="14" />
        <feOffset dx="6" dy="14" />
        <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.65 0" />
        <feComposite in="SourceGraphic" operator="over" />
      </filter>
    </defs>
  );
}

// ----- VaultDoor (main composition) -----------------------------------------

export function VaultDoor({ state, size = 560 }: VaultDoorProps): JSX.Element {
  const T = VB_TOKENS;
  const reduced = useReducedMotion();

  const {
    t,
    dialAngle,
    tension,
    danger,
    keyStates,
    hasKeys,
    keyAngles,
    keySmokes,
    openMode,
    openProgress,
  } = state;

  // ----- Geometry -----
  const outerR = size * 0.46;
  const bezelR = outerR;
  const faceR = outerR - 14;
  const filigreeR = faceR - 8;
  const boltR = faceR - 4;
  const innerRingR = faceR - 32;
  const dialR = innerRingR - 50;
  const keyholeDist = (innerRingR + dialR) / 2 + 4;

  // Pentagon angles — start at top (-PI/2), step CW by 72°.
  const pentaAngles = [0, 1, 2, 3, 4].map(
    (i) => -Math.PI / 2 + i * (VB_TAU / 5)
  );

  // ----- Door swing -----
  // WIN tilts TOWARD camera (rotateY -70deg at full progress),
  // BUST swings AWAY (+70deg). Pivot is the right-edge hinge column.
  const swingSign = openMode === "win" ? -1 : openMode === "bust" ? 1 : 0;
  const swingDeg = swingSign * openProgress * 70;
  const tunnelOpacity = smooth(clamp(openProgress * 1.4, 0, 1));

  // viewBox centered on (0,0) so geometry can use signed coordinates.
  const half = size / 2;
  const viewBox = `${-half} ${-half} ${size} ${size}`;

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: size,
        height: size,
        transform: "translate(-50%, -50%)",
        perspective: "1400px",
        pointerEvents: "none",
      }}
    >
      {/* TUNNEL behind the door — only rendered while opening */}
      {openMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            opacity: tunnelOpacity,
          }}
        >
          <svg
            width={size}
            height={size}
            viewBox={viewBox}
            style={{ overflow: "visible" }}
          >
            <defs>
              <radialGradient
                id="vd-tunnelInnerBg"
                cx="0.5"
                cy="0.5"
                r="0.5"
              >
                {openMode === "win" ? (
                  <>
                    <stop offset="0" stopColor={T.goldHot} />
                    <stop offset="0.2" stopColor={T.gold5} />
                    <stop offset="0.5" stopColor={T.gold3} />
                    <stop offset="0.85" stopColor={T.gold0} />
                    <stop offset="1" stopColor="#000" />
                  </>
                ) : (
                  <>
                    <stop offset="0" stopColor={T.cyan5} />
                    <stop offset="0.25" stopColor={T.cyan3} />
                    <stop offset="0.6" stopColor={T.cyan0} />
                    <stop offset="1" stopColor="#000" />
                  </>
                )}
              </radialGradient>
            </defs>
            <circle r={faceR} fill="url(#vd-tunnelInnerBg)" />
            {/* Concentric rings receding into the tunnel */}
            {[0.85, 0.7, 0.55, 0.4, 0.25].map((s, i) => (
              <circle
                key={i}
                r={faceR * s}
                fill="none"
                stroke={openMode === "win" ? T.gold4 : T.cyan4}
                strokeWidth={1.5 - i * 0.15}
                opacity={(0.5 - i * 0.08) * tunnelOpacity}
              />
            ))}
            {/* Bright core — focuses as the door opens */}
            <circle
              r={faceR * 0.15}
              fill={openMode === "win" ? T.goldHot : T.cyan5}
              opacity={tunnelOpacity}
              style={{
                filter: `blur(${4 + (1 - tunnelOpacity) * 12}px)`,
              }}
            />
          </svg>
        </div>
      )}

      {/* THE DOOR ITSELF — rotateY around the right-edge hinge column */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transformOrigin: `${size * 0.5 + outerR}px 50%`,
          transform: `rotateY(${swingDeg}deg)`,
          transformStyle: "preserve-3d",
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox={viewBox}
          style={{
            overflow: "visible",
            position: "absolute",
            inset: 0,
          }}
        >
          <VaultDefs openMode={openMode} />

          {/* Outer drop shadow */}
          <circle
            r={outerR + 6}
            fill="#000"
            opacity={0.55}
            filter="url(#vd-drop)"
          />

          {/* Bezel ring */}
          <circle
            r={bezelR}
            fill="url(#vd-rim)"
            stroke={T.steel0}
            strokeWidth={2}
          />

          {/* Brushed face */}
          <circle
            r={faceR}
            fill="url(#vd-steel)"
            stroke={T.steel0}
            strokeWidth={1.2}
          />
          <circle r={faceR - 2} fill="url(#vd-brushed)" opacity={0.5} />

          {/* Three concentric engraved rings */}
          <circle
            r={faceR - 4}
            fill="none"
            stroke={T.steel0}
            strokeWidth={0.8}
            opacity={0.7}
          />
          <circle
            r={filigreeR}
            fill="none"
            stroke={T.gold0}
            strokeWidth={0.6}
            opacity={0.5}
          />
          <circle
            r={filigreeR + 6}
            fill="none"
            stroke={T.gold0}
            strokeWidth={0.6}
            opacity={0.5}
          />

          {/* Gold filigree band */}
          <FiligreeRing r={filigreeR} count={36} amplitude={5} />

          {/* Bolt ring */}
          <BoltRing r={boltR} count={20} size={4.5} />

          {/* Three inner rings around the pentagon area — opacity tracks tension */}
          <circle
            r={innerRingR + 4}
            fill="none"
            stroke={T.gold1}
            strokeWidth={1}
            opacity={0.7 * (0.5 + 0.5 * tension)}
          />
          <circle
            r={innerRingR}
            fill="none"
            stroke={T.steel0}
            strokeWidth={1.5}
            opacity={0.5 + 0.5 * tension}
          />
          <circle
            r={innerRingR - 2}
            fill="none"
            stroke={T.steel4}
            strokeWidth={0.5}
            opacity={0.5 * (0.5 + 0.5 * tension)}
          />

          {/* 5 keyholes in a pentagon */}
          {pentaAngles.map((a, i) => (
            <Keyhole
              key={i}
              angle={a}
              dist={keyholeDist}
              index={i}
              state={keyStates[i] ?? "idle"}
              keyAngle={keyAngles[i] ?? 0}
              hasKey={hasKeys[i] ?? false}
              smoke={keySmokes[i] ?? 0}
              t={t}
              reduced={reduced}
            />
          ))}

          {/* Pentagon connecting line — dashed cyan, brightens with tension */}
          <g opacity={0.2 + tension * 0.5}>
            <polygon
              points={pentaAngles
                .map(
                  (a) =>
                    `${Math.cos(a) * keyholeDist},${
                      Math.sin(a) * keyholeDist
                    }`
                )
                .join(" ")}
              fill="none"
              stroke={T.cyan3}
              strokeWidth={0.8}
              strokeDasharray="3 3"
              style={{ filter: `drop-shadow(0 0 6px ${T.cyan3})` }}
            />
          </g>

          {/* Central rotating dial */}
          <CentralDial
            r={dialR}
            dialAngle={dialAngle}
            glow={tension}
            danger={danger}
            t={t}
            reduced={reduced}
          />

          {/* Right-side hinges */}
          <Hinges outerR={outerR} />

          {/* Specular highlights — upper-left */}
          <ellipse
            cx={-faceR * 0.25}
            cy={-faceR * 0.45}
            rx={faceR * 0.65}
            ry={faceR * 0.18}
            fill={T.steel5}
            opacity={0.18}
            style={{ filter: "blur(20px)" }}
          />
          <ellipse
            cx={-faceR * 0.2}
            cy={-faceR * 0.5}
            rx={faceR * 0.35}
            ry={faceR * 0.08}
            fill={T.steel5}
            opacity={0.32}
            style={{ filter: "blur(8px)" }}
          />
        </svg>
      </div>
    </div>
  );
}
